import { Box, Button, useToast } from "@chakra-ui/react";
import { ApiMessageStack, Message, TableV2 } from "components";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { AlertStatusEnum } from "enums/alert-status.enum";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { keyBy } from "lodash";
import { observer } from "mobx-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { TRexSyncLeadSourceExtendedData } from "types/rex.type";
import { syncRexLeadSourceTableColumnDef } from "./syncRexLeadSourceTableColumnDef";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";
import { SyncStatusFilter } from "../../SyncStatusFilter";

const DEFAULT_PAGE_SIZE = 10;

interface IProps {
  accountStore: UserAccountStore;
}

export const RexIntegrationLeadSourcesConfigurator: FC<IProps> = observer(
  ({ accountStore }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<number, TRexSyncLeadSourceExtendedData>
    >({});

    const accountIntegrationsStore = accountStore.accountIntegrationsStore;
    const accountIntegrationRexLeadSourcesStore =
      accountIntegrationsStore.accountIntegrationRexStore;
    const {
      rexSyncLeadSourceData,
      getSyncStatusFilter,
      setSyncStatusFilter,
      filteredRexSyncLeadSourceData,
    } = accountIntegrationRexLeadSourcesStore;

    const updateSyncPair = useCallback(
      (syncPair: TRexSyncLeadSourceExtendedData) => {
        setNewRelations({
          ...newRelations,
          [syncPair.leadSourceId]: syncPair,
        });
      },
      [setNewRelations, newRelations]
    );

    useEffect(() => {
      const suggestedArray = rexSyncLeadSourceData.filter(
        syncData =>
          syncData.syncStatus === IntegrationEntitySyncStatusEnum.SUGGESTED_SYNC
      );
      const suggestedMap = keyBy<TRexSyncLeadSourceExtendedData>(
        suggestedArray,
        syncData => syncData.leadSourceId
      );
      setNewRelations({
        ...suggestedMap,
      });
    }, [rexSyncLeadSourceData]);

    const columnsDef = useMemo(() => {
      return syncRexLeadSourceTableColumnDef(accountStore, updateSyncPair);
    }, [accountStore, updateSyncPair]);

    const dataSource = useMemo(() => {
      return filteredRexSyncLeadSourceData.map(syncData => {
        if (!!newRelations[syncData.leadSourceId])
          return newRelations[syncData.leadSourceId];

        return syncData;
      });
    }, [filteredRexSyncLeadSourceData, newRelations]);

    const submitSyncPairs = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await accountIntegrationRexLeadSourcesStore.updateAccountRexSyncedLeadSources(
          Object.values(newRelations)
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={
                "Rex integration lead sources configuration updated"
              }
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [newRelations, toast, accountIntegrationRexLeadSourcesStore]);

    const banner = useMemo(() => {
      const notSyncedArray = rexSyncLeadSourceData.filter(
        syncData =>
          syncData.syncStatus === IntegrationEntitySyncStatusEnum.NOT_SYNCED
      );

      if (notSyncedArray.length > 0) {
        return (
          <Message status={AlertStatusEnum.WARNING}>
            <Box>
              {`${notSyncedArray.length} of your lead sources are not syncing to LeadPro`}
            </Box>
          </Message>
        );
      }

      return null;
    }, [rexSyncLeadSourceData]);

    const handleSyncFilterChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSyncStatusFilter(e.target.checked);
      },
      [setSyncStatusFilter]
    );
    return (
      <IntegrationConfiguratorLayout>
        <Box fontSize={"xl"}>Lead source mappings</Box>
        {banner}
        <TableV2<TRexSyncLeadSourceExtendedData>
          globalFilterInputPlaceholder={"Search sources by name"}
          dataSource={dataSource}
          columns={columnsDef}
          additionalActions={
            <SyncStatusFilter
              isChecked={getSyncStatusFilter}
              onChange={handleSyncFilterChange}
            />
          }
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
